import React, { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/apiClient";
import { useAuth } from "@/lib/AuthContext";
import useBase44Sync from "@/lib/useBase44Sync";
import { useCabinetLogoDataUri } from "@/components/CDLogo";
import { avery5163SheetHtml, printAvery5163Web, expandLabelItems, isTrimCategory, pickOrderShippingAddress } from "@/lib/labelHtml";
import LabelPickerControls from "@/components/labels/LabelPickerControls";
import ModificationHistory from "@/components/order/ModificationHistory";
import RevisionBadge from "@/components/order/RevisionBadge";
import { hasUnseenModification, itemRevisionState } from "@/lib/orderRevision";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Printer } from "@phosphor-icons/react";

export default function LabelPrint() {
  const { orderId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();
  const logoDataUri = useCabinetLogoDataUri();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncingMissing, setSyncingMissing] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [startOffset, setStartOffset] = useState(0);
  // Bumped after `Mark as reviewed` is clicked so any derived state that
  // reads localStorage (via hasUnseenModification) re-computes.
  const [ackVersion, setAckVersion] = useState(0);
  const iframeRef = useRef(null);
  const autoPrintFiredRef = useRef(false);
  const syncTriedRef = useRef(null);

  const { canSync } = useBase44Sync();

  // The remote backend's GET /api/orders/{id} returns null for several Base44
  // meta fields (specialty summary, store, shipping_address, etc.) that the
  // list endpoint serializes correctly. We fetch both in parallel and overlay
  // any missing summary fields from the list version so labels see the full
  // order data.
  const fetchOnce = useCallback(async () => {
    try {
      const [detailRes, activeListRes, completedListRes] = await Promise.all([
        api.get(`/orders/${orderId}`).catch((e) => {
          if (e?.response?.status === 404) return null;
          throw e;
        }),
        api.get(`/orders`, { params: { status: "active" } }).catch(() => null),
        api.get(`/orders`, { params: { status: "completed" } }).catch(() => null),
      ]);
      const detail = detailRes ? detailRes.data : null;
      if (!detail) return null;
      const listData = [
        ...((activeListRes && activeListRes.data) || []),
        ...((completedListRes && completedListRes.data) || []),
      ];
      const summary = listData.find((o) => o && o.id === orderId);
      if (!summary) return detail;
      return mergeOrderForLabels(detail, summary);
    } catch (err) {
      console.warn("LabelPrint: fetch failed", err);
      return null;
    }
  }, [orderId]);

  const fetchOrder = useCallback(async () => {
    setLoading(true);
    try {
      let data = await fetchOnce();
      // Auto-sync once if the order isn't in the local DB yet (foolproofs
      // deep-links from the ordering system that point at brand-new orders).
      if (!data && canSync && syncTriedRef.current !== orderId) {
        syncTriedRef.current = orderId;
        setSyncingMissing(true);
        try {
          await api.post("/base44/sync");
          data = await fetchOnce();
          if (data) toast.success("Found via Base44 sync");
        } catch (err) {
          console.warn("LabelPrint: Base44 sync failed", err);
        } finally {
          setSyncingMissing(false);
        }
      }
      if (data) {
        setOrder(data);
        // Default selection: everything except cancelled items and
        // upstream-removed items (Base44 dropped them; they're kept only
        // for audit and shouldn't get fresh labels).
        const ids = new Set(
          (data.items || [])
            .filter((i) => !i.cancelled)
            .filter((i) => {
              const st = itemRevisionState(i, data);
              return !(st && st.kind === "removed");
            })
            .map((i) => i.id)
        );
        setSelected(ids);
      } else {
        setOrder(null);
      }
    } finally { setLoading(false); }
  }, [fetchOnce, canSync, orderId]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    syncTriedRef.current = null;
    fetchOrder();
  }, [fetchOrder]);

  const labelItems = useMemo(() => {
    if (!order) return [];
    const selectedItems = (order.items || [])
      .filter((i) => selected.has(i.id))
      .map((i) => ({ ...i, customer_name: i.customer_name || order.customer_name || "" }));
    // Sort: non-trim items first (in original order), trim items last (also
    // in original order). Pickers prefer trim at the end of the sheet so the
    // bulk of cabinets/items print first and they can grab those while trim
    // is still printing.
    const nonTrim = selectedItems.filter((i) => !isTrimCategory(i.category));
    const trim = selectedItems.filter((i) => isTrimCategory(i.category));
    return [...nonTrim, ...trim];
  }, [order, selected]);

  const html = useMemo(
    () => avery5163SheetHtml(labelItems, {
      orderNumber: order?.order_number,
      orderId: order?.id,
      store: order?.store,
      shippingAddress: pickOrderShippingAddress(order),
      startOffset,
      printedBy: user?.name || user?.email,
      logoDataUri,
    }),
    [labelItems, order, startOffset, user?.name, user?.email, logoDataUri]
  );

  useEffect(() => {
    if (iframeRef.current) iframeRef.current.srcdoc = html;
  }, [html]);

  const totalCells = expandLabelItems(labelItems).length + Math.max(0, Math.min(9, startOffset | 0));
  const sheetCount = Math.max(1, Math.ceil(totalCells / 10));

  // Recompute on ackVersion so `Mark as reviewed` immediately toggles the gate.
  const needsReviewGate = useMemo(() => hasUnseenModification(order), [order, ackVersion]);

  const handlePrint = useCallback(async () => {
    if (needsReviewGate) {
      toast.error("Please review the modification history and mark it reviewed before printing.");
      return;
    }
    try { await printAvery5163Web(html); }
    catch (err) { toast.error("Print failed: " + (err?.message || String(err))); }
  }, [html, needsReviewGate]);

  const onAcknowledgeModification = useCallback(() => {
    setAckVersion((v) => v + 1);
  }, []);

  // Auto-print: if the URL carries ?autoprint=1, fire the browser print dialog
  // once the order has loaded and the iframe preview HTML is ready. We then
  // strip the param from the URL so refreshes don't re-trigger it. Guard with
  // a ref so React strict-mode double-effect doesn't print twice. Auto-print
  // is suppressed while a modification-review gate is pending — printing an
  // unreviewed modified order is exactly the bug this flow prevents.
  useEffect(() => {
    if (autoPrintFiredRef.current) return;
    if (loading || !order) return;
    if (!labelItems.length) return;
    if (needsReviewGate) return;
    const flag = searchParams.get("autoprint");
    if (flag !== "1" && flag !== "true") return;
    autoPrintFiredRef.current = true;
    // Small delay so the iframe srcdoc has a moment to load images before snapshot.
    const t = setTimeout(() => {
      handlePrint();
      // Strip the param so a refresh / share doesn't re-trigger the print dialog.
      const next = new URLSearchParams(searchParams);
      next.delete("autoprint");
      setSearchParams(next, { replace: true });
    }, 800);
    return () => clearTimeout(t);
  }, [loading, order, labelItems.length, searchParams, setSearchParams, handlePrint, needsReviewGate]);

  const toggle = useCallback((id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }, []);

  const toggleAll = useCallback(() => {
    if (!order) return;
    const allIds = (order.items || [])
      .filter((i) => !i.cancelled)
      .filter((i) => {
        const st = itemRevisionState(i, order);
        return !(st && st.kind === "removed");
      })
      .map((i) => i.id);
    setSelected((prev) => prev.size === allIds.length ? new Set() : new Set(allIds));
  }, [order]);

  if (loading) {
    return (
      <div className="text-zinc-500" data-testid="label-print-loading">
        {syncingMissing ? (
          <>
            Syncing from Base44 to find this order…
            <div className="text-xs text-zinc-400 mt-1">This can take a few seconds.</div>
          </>
        ) : "Loading order…"}
      </div>
    );
  }
  if (!order) {
    return (
      <div className="space-y-3" data-testid="label-print-not-found">
        <h1 className="font-heading font-black text-2xl">Order not found</h1>
        <p className="text-sm text-zinc-600 dark:text-zinc-300">
          {canSync
            ? "We tried syncing from Base44 but couldn't find this order. The link may be stale, or the order may exist in your other view mode (Live / Test)."
            : "This order isn't synced to the local database yet. Ask an admin to run a Base44 sync, or check that your view mode (Live / Test) matches."}
        </p>
        <Link to="/orders" className="text-[#C8102E] font-semibold hover:underline">
          Go to orders →
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="label-print-page">
      <PageHeader
        order={order}
        sheetCount={sheetCount}
        canPrint={labelItems.length > 0 && !needsReviewGate}
        onPrint={handlePrint}
        needsReview={needsReviewGate}
      />

      <ModificationHistory order={order} onAcknowledge={onAcknowledgeModification} />

      <LabelPickerControls
        order={order}
        selected={selected}
        onToggle={toggle}
        onToggleAll={toggleAll}
        startOffset={startOffset}
        setStartOffset={setStartOffset}
      />

      <PreviewArea
        iframeRef={iframeRef}
        sheetCount={sheetCount}
        empty={labelItems.length === 0}
      />
    </div>
  );
}

function PageHeader({ order, sheetCount, canPrint, onPrint, needsReview }) {
  return (
    <div>
      <Link to={`/orders/${order.id}`} className="inline-flex items-center gap-1 text-sm text-zinc-600 hover:text-zinc-900 font-semibold" data-testid="back-to-order">
        <ArrowLeft size={14} weight="bold" /> Back to order
      </Link>
      <div className="flex flex-wrap items-end justify-between gap-4 mt-2">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500 font-semibold">Print</div>
          <div className="flex items-center gap-2 flex-wrap mt-1">
            <h1 className="font-heading font-black text-3xl sm:text-4xl">
              Labels · {order.order_number}
            </h1>
            <RevisionBadge order={order} size="md" />
          </div>
          <p className="text-sm text-zinc-600 mt-1">
            Avery 5163 · 2&quot; × 4&quot; · 10 per sheet · {sheetCount} sheet{sheetCount === 1 ? "" : "s"} to print
          </p>
        </div>
        <Button
          onClick={onPrint}
          className="bg-[#C8102E] hover:bg-[#A00D25] h-11"
          data-testid="trigger-print"
          disabled={!canPrint}
          title={needsReview ? "Review the modification history first" : undefined}
        >
          <Printer size={18} weight="bold" className="mr-1.5" /> {needsReview ? "Review required" : "Print"}
        </Button>
      </div>
    </div>
  );
}

function PreviewArea({ iframeRef, sheetCount, empty }) {
  return (
    <Card className="p-3 border-zinc-200" data-testid="label-preview-card">
      <div className="flex items-center justify-between mb-2 px-2 pt-1">
        <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 font-semibold">
          Live preview · {sheetCount} sheet{sheetCount === 1 ? "" : "s"} · pixel-identical to mobile app
        </div>
      </div>
      {empty ? (
        <div className="p-12 text-center text-zinc-500">Select at least one item to preview.</div>
      ) : (
        <div className="bg-zinc-200 p-4 rounded-md overflow-auto" style={{ maxHeight: "75vh" }}>
          <iframe
            ref={iframeRef}
            title="Label preview"
            data-testid="label-preview-iframe"
            style={{
              width: "8.5in",
              transform: "scale(0.85)",
              transformOrigin: "top left",
              height: `${11 * sheetCount}in`,
              border: "1px solid #D4D4D8",
              background: "white",
              display: "block",
              margin: "0 auto",
            }}
          />
          <div style={{ height: `${11 * sheetCount * 0.85 - 11 * sheetCount}in` }} />
        </div>
      )}
    </Card>
  );
}

// Fields that the production detail endpoint sometimes drops but the list
// endpoint serializes correctly. We overlay only when detail value is null/
// undefined so detail-endpoint values are preserved when present.
const LABEL_SUMMARY_FALLBACK_FIELDS = [
  "shipping_address",
  "delivery_address",
  "ship_to_address",
  "ship_to",
  "unit_number",
  "unit",
  "suite",
  "apt",
  "store",
  "customer_name",
  "customer_email",
  "delivery_date",
  "po_number",
  "revision_number",
  "modification_log",
  "updated_date",
];

function mergeOrderForLabels(detail, summary) {
  if (!summary) return detail;
  const merged = { ...detail };
  for (const key of LABEL_SUMMARY_FALLBACK_FIELDS) {
    if (merged[key] == null && summary[key] != null) {
      merged[key] = summary[key];
    }
  }
  return merged;
}

